// frontend/src/auth/referralCapture.ts
// Bugfix #37: Referral-Code aus ?ref= fruehzeitig persistieren, damit beide
// Register-Flows (Web-Login + Native-Onboarding) den Code an POST /api/auth/register
// mitsenden koennen und der Werber spaeter belohnt wird.
//
// Einladungslinks werden serverseitig in server/referral.js:53 als
// `${appUrl}/?ref=${row.code}` erzeugt. Der Server akzeptiert im Register-Body
// sowohl `referral_code` (User-zu-User) als auch `affiliate_code` (Affiliate-
// Programm). Wir erfassen hier NUR `?ref=` (referral_code), weil:
//   - Im gesamten Codebase KEIN `?aff=`- oder vergleichbarer Affiliate-URL-
//     Parameter definiert ist (nicht raten, siehe Aufgabe).
//   - Der Server unterscheidet die beiden Felder; ein falsch befuelltes
//     `affiliate_code` wuerde sonst einen Affiliate-Match versuchen und
//     stillschweigend scheitern (kein Hard-Fail).
//
// Speicher: window.localStorage. Auf Capacitor-Native ist die URL in der
// Regel `capacitor://localhost` ohne Query-Params — Capture ist dann no-op.
// Wir nutzen absichtlich NICHT Capacitor-Preferences: das Modul laeuft auch
// im Web und vor dem Auth-Bootstrap.

const PENDING_REFERRAL_KEY = 'lorath_pending_referral_code';

interface PendingReferral {
  referral_code?: string;
}

function getWebStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    // Privater Modus / Storage-Disable — Capture ist best-effort.
    return null;
  }
}

function readItem(key: string): string | null {
  const storage = getWebStorage();
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeItem(key: string, value: string): void {
  const storage = getWebStorage();
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    // Quota / SecurityError — Capture ist best-effort.
  }
}

function removeItem(key: string): void {
  const storage = getWebStorage();
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // Best-effort.
  }
}

// Persistiert einen vorhandenen, nicht-leeren ?ref=-Wert aus einem
// beliebigen search-String nach localStorage. Bestehende Werte werden
// nicht mit leeren Parametern ueberschrieben (z.B. wenn der User spaeter
// auf eine Seite ohne ?ref= surft und der Effekt erneut feuert).
//
// Wiederverwendet vom Web-Cold-Start (captureReferralFromUrl) und vom
// nativen Deep-Link-Handler (deepLinks.dispatchDeepLink), damit der
// Werber auch fuer nativ geworbene Nutzer belohnt wird (Audit 05-P1-13).
export function captureReferralFromSearch(search: string): void {
  const params = new URLSearchParams(search);
  const ref = (params.get('ref') ?? '').trim();
  if (!ref) return;
  writeItem(PENDING_REFERRAL_KEY, ref);
}

// Convenience-Wrapper fuer den Web-Cold-Start: liest window.location.search
// einmalig und delegiert an captureReferralFromSearch.
//
// Muss VOR React Router aufgerufen werden, weil der Router window.location
// bei Bedarf normalisiert (search erschoepfen koennte). Sicherer Aufrufer:
// frontend/src/main.tsx direkt vor <App/>.
export function captureReferralFromUrl(): void {
  if (typeof window === 'undefined') return;
  captureReferralFromSearch(window.location.search);
}

// Liefert den gemerkten Referral-Code (oder leeres Objekt). Wird in beiden
// Register-Flows aufgerufen, um den Body konditional zu befuellen.
export function getPendingReferral(): PendingReferral {
  const code = readItem(PENDING_REFERRAL_KEY);
  if (!code) return {};
  return { referral_code: code };
}

// Nach erfolgreicher Registrierung aufrufen — sonst wuerde der Code bei
// einer spaeteren Zweitregistrierung am selben Geraet erneut greifen.
export function clearPendingReferral(): void {
  removeItem(PENDING_REFERRAL_KEY);
}
